import styled from 'styled-components';
import { useSelector } from 'src/store/store';
import i18n from 'src/utils/translate';
import { translationWithLink } from 'src/utils/i18n/translationUtils';
import membersFilterOptions from 'src/constants/membersFilterOptions';
import { redirectToMembersPage } from 'src/utils/router';
import PostFilterInline from 'src/components/postFilterInline';
import CommunityMemberCard from 'src/modules/community/communityMembers/communityMemberCard';
import ImproveSimilarToMeRecommendationsCard from 'src/modules/community/communityMembers/improveSimilarToMeRecommendationsCard';
import CommunityCardLoader from 'src/modules/search/communities/components/communityCardLoader';
import Pagination from 'src/components/pagination/pagination';
import paginationResultsPerPage from 'src/constants/paginationResultsPerPage';
import SearchBar from 'src/modules/search/shared/components/searchBar';
import PrivatePageErrorMessage from 'src/components/privatePageErrorMessage';
import { trackFootfallEvent } from 'src/utils/footfall';
import { Event, Section } from 'src/constants/footfall';
import { helpScoutArticle } from 'src/utils/helpScoutBeacon';
import { LinkButton } from 'styles-js/buttons';
import { titleMedium, titleSmall } from 'styles-js/mixins/typography';

export default function CommunityMembersContent() {
  const { slug: communitySlug, name: communityName } = useSelector(({ context }) => context.currentCommunity);
  const currentUser = useSelector(({ context }) => context.currentUser);
  const { currentFilter: filter, data: { members, total }, isLoading } = useSelector(({ pages }) => pages.communityMembers);

  const handleFilterClick = (newFilter) => {
    let filterName = newFilter;
    if (newFilter === 'roles') filterName = 'admins and moderators';
    if (newFilter === 'similar-to-me') filterName = 'similar to me';
    redirectToMembersPage(communitySlug, newFilter);
    trackFootfallEvent(Event.CLICKED, {
      clickType: 'community-members-filter',
      clickMetadata: { section: Section.CommunityMembers, element: filterName },
    });
  };

  const renderEmptyState = () => {
    if (filter === 'similar-to-me') {
      return emptyStateSimilarToMe();
    } else if (filter === 'roles') {
      return emptyStateModerationTeam();
    } else {
      return emptyState();
    }
  };

  const emptyState = () => (
    <Empty>
      {translationWithLink(i18n.t('No one here yet! %1$sInvite someone now%2$s'), {
        href: `mailto:?subject=${communityName}&body=I saw this new community and I thought you would like it: https://healthunlocked.com/${communitySlug}?ref=invite`,
      })}
    </Empty>
  );

  const emptyStateSimilarToMe = () => (
    <Empty>
      <p>{i18n.t('No one here yet!')}</p>
      <ImproveSimilarToMeRecommendationsCard />
    </Empty>
  );

  const emptyStateModerationTeam = () => (
    <Empty>
      {i18n.t('This community is currently being moderated by HealthUnlocked: reports of abuse are reviewed and actioned when appropriate.')}{' '}
      <LinkButton onClick={() => helpScoutArticle('193-communities-moderated-by-healthunlocked')}>{i18n.t('Learn how')}{' >'}</LinkButton>
    </Empty>
  );

  const renderMembersList = () => (
    <>
      <CommunityMembersList>
        {members.map(member => (
          <CommunityMemberCard key={member.username} member={member} />
        ))}
      </CommunityMembersList>
      <Pagination
        pageSize={paginationResultsPerPage.searchMembers}
        total={total}
      />
      {filter === 'similar-to-me' && <ImproveSimilarToMeRecommendationsCard />}
    </>
  );

  return (
    <>
      <SearchBarContainer>
        <SearchBar
          autoFocus={true}
          disableAutosuggest={true}
          isCommunitySearch={true}
          placeholderText={i18n.t('Search members on ') + communityName}
          searchType="members"
        />
      </SearchBarContainer>

      <Filters>
        <p>{i18n.t('Sort members by')}:</p>
        <PostFilterInline
          options={membersFilterOptions}
          selected={filter}
          onFilterClick={handleFilterClick}
        />
      </Filters>

      {!currentUser
        ? <PrivatePageErrorMessage />
        : isLoading
          ? <CommunityCardLoader height={245} />
          : members && members.length > 0 ? renderMembersList() : renderEmptyState()
      }
    </>
  );
}

const Empty = styled.div`
  margin: 30px 0;
  width: calc(60% + 12px);
  @media (${({ theme }) => theme.underScreenSmall}) {
    width: 100%;
  }
  & > p {
    ${titleMedium};
    margin-bottom: 25px;
  }
`;

const CommunityMembersList = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const SearchBarContainer = styled.div`
  width: calc(60% + 12px);
  @media (${({ theme }) => theme.underScreenSmall}) {
    width: 100%;
  }
  .search-bar {
    margin-bottom: 25px;
  }
  .search-bar__container {
    max-width: none;
  }
`;

const Filters = styled.div`
  margin-bottom: 20px;
  & > p {
    ${titleSmall};
    margin-bottom: 8px;
  }
`;
